import {
  highlightElement,
  pointTo,
  registerAction,
  scrollTo,
} from './actions';
import { mountWidget } from './ui';

const VERSION = '0.1.0';

function findSiteId(): string | null {
  const fromGlobal = (window as Window & { __GENIOUS_SITE_ID__?: string }).__GENIOUS_SITE_ID__;
  if (fromGlobal) return fromGlobal;

  const current = document.currentScript as HTMLScriptElement | null;
  if (current?.dataset.siteId) return current.dataset.siteId;

  const scripts = Array.from(
    document.querySelectorAll('script[data-site-id], script[data-genious-site]')
  );
  for (const s of scripts) {
    const id =
      s.getAttribute('data-site-id') || s.getAttribute('data-genious-site');
    if (id) return id;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('geniousSiteId') || params.get('siteId');
}

/**
 * Capturé À L'EXÉCUTION DU MODULE, et pas plus tard.
 *
 * `document.currentScript` n'est renseigné que pendant l'exécution synchrone du
 * script. Lu depuis un `setTimeout` — c'est-à-dire partout ailleurs dans ce
 * fichier — il vaut invariablement `null`, et le test qui s'appuie dessus ne
 * s'exécute jamais. Le piège est silencieux : le code a l'air juste et la
 * branche est morte.
 */
const SCRIPT_COURANT = document.currentScript as HTMLScriptElement | null;

/**
 * Démarrage différé — RGPD, ticket C9.
 *
 * Par défaut le widget démarre seul : c'est le comportement historique, et le
 * changer casserait toutes les installations existantes chez les clients.
 *
 * Mais démarrer seul écrit en base AVANT que le visiteur n'ait rien demandé
 * (POST /runtime/session crée une ligne `conversations`), et l'exemption
 * « service expressément demandé » de l'article 82 LIL ne couvre pas ce
 * moment-là. Le site hôte — qui est le responsable de traitement, pas nous —
 * doit donc pouvoir retarder ce démarrage jusqu'à son propre feu vert.
 *
 * Avec `data-genious-demarrage="manuel"` sur le script : aucun appel réseau,
 * aucune écriture dans le navigateur, aucune ligne en base. Le site appelle
 * `Genious.mount("gns_…")` une fois le consentement recueilli.
 *
 * L'API publique `mount()` existait déjà : il n'y a rien à inventer, seulement
 * à ne pas appeler `boot()` tout seul.
 */
function demarrageManuel(): boolean {
  if (SCRIPT_COURANT?.dataset.geniousDemarrage) {
    return SCRIPT_COURANT.dataset.geniousDemarrage === 'manuel';
  }

  // Repli sur la recherche par attribut : le script peut avoir été injecté
  // dynamiquement, auquel cas SCRIPT_COURANT ne le désigne pas.
  const balise = document.querySelector('script[data-genious-demarrage]');
  return balise?.getAttribute('data-genious-demarrage') === 'manuel';
}

/**
 * Lecture des réponses à voix haute, proposée seulement si le site la demande
 * (`data-voix="on"`). L'API a son propre interrupteur : cet attribut affiche le
 * bouton, il n'autorise aucune dépense.
 */
function voixDemandee(): boolean {
  if (SCRIPT_COURANT?.dataset.voix) return SCRIPT_COURANT.dataset.voix === 'on';
  const balise = document.querySelector('script[data-voix]');
  return balise?.getAttribute('data-voix') === 'on';
}

/**
 * Le visiteur peut parler au widget, si le site le demande (`data-micro="on"`).
 * Même logique que la voix : l'attribut montre le bouton, l'API décide.
 */
function microDemande(): boolean {
  if (SCRIPT_COURANT?.dataset.micro) return SCRIPT_COURANT.dataset.micro === 'on';
  const balise = document.querySelector('script[data-micro]');
  return balise?.getAttribute('data-micro') === 'on';
}

let booting = false;

async function boot(explicitSiteId?: string) {
  const existing = document.getElementById('genious-root');
  if (existing) {
    GeniousAPI.mounted = true;
    return window.Genious;
  }
  // Root disparu (preview Studio / Strict Mode) → autoriser un remount
  GeniousAPI.mounted = false;
  if (booting) return window.Genious;
  booting = true;

  try {
    const siteId = explicitSiteId || findSiteId();
    if (!siteId) {
      console.warn('[GENIOUS] data-site-id manquant — appeltez Genious.mount("gns_…").');
      return window.Genious;
    }

    const api = await mountWidget(siteId, { voix: voixDemandee(), micro: microDemande() });
    Object.assign(GeniousAPI, {
      siteId,
      visitorId: api.visitorId ?? null,
      open: api.open,
      close: api.close,
      mounted: true,
    });
    console.info('[GENIOUS] Personnage monté pour', siteId);
  } catch (err) {
    console.error('[GENIOUS] Échec du montage du personnage', err);
    GeniousAPI.mounted = false;
  } finally {
    booting = false;
  }
  return window.Genious;
}

const GeniousAPI = {
  version: VERSION,
  siteId: null as string | null,
  /**
   * Identifiant du visiteur pour la session en cours, ou `null`.
   * Exposé pour que la personne puisse désigner ses données auprès de
   * l'exploitant du site. Meurt avec l'onglet — voir api.ts.
   */
  visitorId: null as string | null,
  mounted: false,
  open: () => undefined as void,
  close: () => undefined as void,
  mount: (siteId: string) => boot(siteId),
  registerAction,
  scrollTo,
  highlightElement,
  pointTo,
};

window.Genious = GeniousAPI;

function demarrerSiAutorise() {
  if (demarrageManuel()) {
    console.info(
      '[GENIOUS] Démarrage manuel demandé — appelez Genious.mount("gns_…") ' +
        'après le recueil du consentement. Aucune donnée collectée à ce stade.'
    );
    return;
  }
  void boot();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.setTimeout(demarrerSiAutorise, 0);
  });
} else {
  window.setTimeout(demarrerSiAutorise, 0);
}

export default GeniousAPI;
